import React, { useEffect, useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { createRoom, getRoom, getRoomTags, updateRoom, uploadRoomDocument, uploadRoomImage, type Room, type RoomTag } from '../../lib/rooms';
import { useGeoapifyAddressAutocomplete } from '../../hooks/useGeoapifyAddressAutocomplete';
import { useStandort } from '../layouts/use-standort';
import { api } from '../../lib/api';

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Gleiche Normalisierung wie im Backend: Rand-Leerzeichen weg, innen einfache Leerzeichen.
const normalizeRoomName = (value: string): string => value.trim().replace(/\s+/g, ' ');

// Standorte haben nur ein Freitext-Adressfeld; der Wizard braucht die Teile einzeln.
const splitAddress = (value: string) => {
  const parts = value.split(/[,\n]/).map(p => p.trim()).filter(Boolean);
  const streetPart = parts[0] || '';
  const cityPart = parts.slice(1).join(' ').trim();
  const streetMatch = streetPart.match(/^(.*?)[\s]+(\d+\s*[a-zA-Z]?)$/);
  const cityMatch = cityPart.match(/^(\d{4,5})\s+(.*)$/);
  return {
    street: (streetMatch ? streetMatch[1] : streetPart).trim(),
    houseNumber: streetMatch ? streetMatch[2].replace(/\s+/g, '') : '',
    postalCode: cityMatch ? cityMatch[1] : '',
    city: (cityMatch ? cityMatch[2] : cityPart).trim(),
  };
};
type CostInterval = 'hour' | 'day' | 'usage' | 'month';

export interface RoomCreateWizardModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  standortId: number;
  resumeRoomId?: number; // Draft-Raum, der weiterbearbeitet wird
  onCreated?: (room: Room) => void;
}

export const RoomCreateWizardModal: React.FC<RoomCreateWizardModalProps> = ({ open, onOpenChange, standortId, resumeRoomId, onCreated }) => {
  const { standorte } = useStandort();
  const standortName = standorte.find(s => s.id === standortId)?.name || `Standort #${standortId}`;

  const [step, setStep] = useState<Step>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const [roomId, setRoomId] = useState<number | null>(null);
  const [availableTags, setAvailableTags] = useState<RoomTag[]>([]);

  // Schritt 1
  const [name, setName] = useState('');
  const [internalNote, setInternalNote] = useState('');

  // Schritt 2
  const [addressSearch, setAddressSearch] = useState('');
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [addressSearchFocused, setAddressSearchFocused] = useState(false);
  const [addressSameAsStandort, setAddressSameAsStandort] = useState(false);
  const [notPublicAccessible, setNotPublicAccessible] = useState(false);
  const [directions, setDirections] = useState('');
  const [addressFormatted, setAddressFormatted] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');

  // Schritt 3
  const [capacity, setCapacity] = useState<number>(1);
  const [sizeM2, setSizeM2] = useState<number | ''>('');

  // Schritt 4
  const [costEuros, setCostEuros] = useState<string>('');
  const [costInterval, setCostInterval] = useState<CostInterval | ''>('');

  // Schritt 5
  const [tagIds, setTagIds] = useState<number[]>([]);

  // Schritt 6
  const [images, setImages] = useState<Array<{ url: string; name?: string }>>([]);
  const [documents, setDocuments] = useState<Array<{ url: string; name?: string }>>([]);
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');

  const { suggestions: addressSuggestions, apiKeyPresent } = useGeoapifyAddressAutocomplete({
    query: addressSearch,
    enabled: open && step === 2,
  });

  const requiredStep1Ok = useMemo(() => normalizeRoomName(name).length > 0, [name]);
  const requiredStep2Ok = useMemo(() => {
    return street.trim() !== '' && houseNumber.trim() !== '' && postalCode.trim() !== '' && city.trim() !== '';
  }, [street, houseNumber, postalCode, city]);
  const requiredStep3Ok = useMemo(() => Number.isFinite(capacity) && capacity >= 1, [capacity]);

  const canNext = useMemo(() => {
    if (step === 1) return requiredStep1Ok;
    if (step === 2) return requiredStep2Ok;
    if (step === 3) return requiredStep3Ok;
    return true;
  }, [requiredStep1Ok, requiredStep2Ok, requiredStep3Ok, step]);

  const canFinish = useMemo(() => requiredStep1Ok && requiredStep2Ok && requiredStep3Ok, [requiredStep1Ok, requiredStep2Ok, requiredStep3Ok]);

  const isDirty = useMemo(() => {
    return (
      !!roomId ||
      name.trim() !== '' ||
      internalNote.trim() !== '' ||
      addressSearch.trim() !== '' ||
      addressFormatted.trim() !== '' ||
      street.trim() !== '' ||
      houseNumber.trim() !== '' ||
      postalCode.trim() !== '' ||
      city.trim() !== '' ||
      directions.trim() !== '' ||
      notPublicAccessible ||
      capacity !== 1 ||
      sizeM2 !== '' ||
      costEuros.trim() !== '' ||
      costInterval !== '' ||
      tagIds.length > 0 ||
      images.length > 0 ||
      documents.length > 0
    );
  }, [roomId, name, internalNote, addressSearch, addressFormatted, street, houseNumber, postalCode, city, directions, notPublicAccessible, capacity, sizeM2, costEuros, costInterval, tagIds, images.length, documents.length]);

  const toCostCents = (): number | null => {
    const val = costEuros.trim();
    if (val === '') return null;
    const num = Number(val.replace(',', '.'));
    if (!Number.isFinite(num)) return null;
    return Math.max(0, Math.round(num * 100));
  };

  const buildDraftPayload = (wizardStep: number) => {
    const costCents = toCostCents();
    return {
      standort_id: standortId,
      name: normalizeRoomName(name),
      kapazitaet: requiredStep3Ok ? capacity : (capacity || 1),
      status: 'draft' as const,
      wizard_step: wizardStep,
      internal_note: internalNote.trim() || null,
      not_public_accessible: notPublicAccessible,
      address: addressFormatted || null,
      address_street: street || null,
      address_house_number: houseNumber || null,
      address_postal_code: postalCode || null,
      address_city: city || null,
      directions: directions || null,
      image_url: coverImageUrl || null,
      images,
      documents,
      size_m2: typeof sizeM2 === 'number' ? sizeM2 : null,
      cost_cents: costCents,
      cost_interval: costInterval || null,
      tag_ids: tagIds,
      aktiv: false,
    } as const;
  };

  const saveDraft = async (nextWizardStep: number) => {
    if (!requiredStep1Ok) {
      toast.error('Bitte zuerst den Raumnamen ausfüllen');
      return null;
    }
    setIsLoading(true);
    try {
      if (!roomId) {
        const created = await createRoom(buildDraftPayload(nextWizardStep) as any);
        setRoomId(created.id);
        return created;
      }
      const updated = await updateRoom(roomId, buildDraftPayload(nextWizardStep) as any);
      return updated;
    } catch (e: any) {
      toast.error(e?.message || 'Entwurf konnte nicht gespeichert werden');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const finalize = async () => {
    if (!roomId) {
      // Sollte nicht passieren, weil wir spätestens nach Step1 speichern.
      const created = await saveDraft(7);
      if (!created) return;
    }
    setIsLoading(true);
    try {
      const payload = {
        ...buildDraftPayload(null as any),
        status: 'active' as const,
        wizard_step: null,
        aktiv: true,
      };
      const updated = await updateRoom(roomId as number, payload as any);
      toast.success('Raum wurde erfolgreich angelegt.');
      onOpenChange(false);
      if (onCreated) onCreated(updated);
    } catch (e: any) {
      toast.error(e?.message || 'Raum konnte nicht angelegt werden');
    } finally {
      setIsLoading(false);
    }
  };

  const goNext = async () => {
    if (!canNext) {
      toast.error('Bitte füllen Sie die Pflichtfelder aus');
      return;
    }
    const next = (step + 1) as Step;
    // Speichern als Draft bei Step-Wechsel (ab Step 1)
    const saved = await saveDraft(next);
    if (!saved) return;
    setStep(next);
  };

  const goBack = async () => {
    const prev = (step - 1) as Step;
    if (prev < 1) return;
    // Persistiere den zuletzt offenen Step
    if (roomId) {
      await saveDraft(prev);
    }
    setStep(prev);
  };

  const requestClose = () => {
    if (!isDirty) {
      onOpenChange(false);
      return;
    }
    setConfirmCloseOpen(true);
  };

  // Daten beim Öffnen laden
  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    (async () => {
      try {
        const tags = await getRoomTags().catch(() => []);
        setAvailableTags(tags || []);

        if (resumeRoomId) {
          const r = await getRoom(resumeRoomId);
          setRoomId(r.id);
          setName(r.name || '');
          setInternalNote((r as any).internal_note || '');
          setNotPublicAccessible(!!(r as any).not_public_accessible);
          setDirections(r.directions || '');
          setAddressFormatted(r.address || '');
          setStreet((r as any).address_street || '');
          setHouseNumber((r as any).address_house_number || '');
          setPostalCode((r as any).address_postal_code || '');
          setCity((r as any).address_city || '');
          setCapacity(r.kapazitaet || 1);
          setSizeM2((r.size_m2 ?? '') as any);
          setCoverImageUrl(r.image_url || '');
          setImages((r.images || []).map(f => ({ url: f.url, name: f.name || undefined })));
          setDocuments((r.documents || []).map(f => ({ url: f.url, name: f.name || undefined })));
          setTagIds(r.tag_ids || []);
          const cents = (r as any).cost_cents as number | null | undefined;
          setCostEuros(cents != null ? (cents / 100).toFixed(2) : '');
          setCostInterval(((r as any).cost_interval as any) || '');
          const ws = (r as any).wizard_step as number | null | undefined;
          const nextStep = (ws && ws >= 1 && ws <= 7 ? ws : 1) as Step;
          setStep(nextStep);
        } else {
          // new wizard
          setRoomId(null);
          setStep(1);
          setName('');
          setInternalNote('');
          setAddressSearch('');
          setAddressSameAsStandort(false);
          setNotPublicAccessible(false);
          setDirections('');
          setAddressFormatted('');
          setStreet('');
          setHouseNumber('');
          setPostalCode('');
          setCity('');
          setCapacity(1);
          setSizeM2('');
          setCostEuros('');
          setCostInterval('');
          setTagIds([]);
          setImages([]);
          setDocuments([]);
          setCoverImageUrl('');
        }
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    })();
  }, [open, resumeRoomId]);

  // Checkbox: Adresse wie Standort. Die Standort-Liste liefert keine Adresse,
  // deshalb das Detail nachladen und die Pflichtfelder direkt befuellen.
  useEffect(() => {
    if (!open || step !== 2) return;
    if (!addressSameAsStandort) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.get<{ adresse?: string | null }>(`/api/standorte/${standortId}`);
        const adresse = (detail?.adresse || '').trim();
        if (cancelled || !adresse) {
          if (!cancelled && !adresse) toast.error('Für diesen Standort ist keine Adresse hinterlegt');
          return;
        }
        const parts = splitAddress(adresse);
        setAddressSearch(adresse);
        setAddressFormatted(adresse);
        setStreet(parts.street);
        setHouseNumber(parts.houseNumber);
        setPostalCode(parts.postalCode);
        setCity(parts.city);
      } catch {
        if (!cancelled) toast.error('Standort-Adresse konnte nicht geladen werden');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addressSameAsStandort, open, standortId, step]);

  const steps = useMemo(
    () => [
      { label: 'Grunddaten' },
      { label: 'Standort' },
      { label: 'Kapazität' },
      { label: 'Kosten' },
      { label: 'Ausstattung' },
      { label: 'Dateien' },
      { label: 'Zusammenfassung' },
    ],
    []
  );

  const toggleTag = (id: number) => {
    setTagIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const uploadFiles = async (files: FileList | null, kind: 'image' | 'document') => {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    setIsLoading(true);
    try {
      const uploaded: Array<{ url: string; name?: string }> = [];
      for (const f of list) {
        const stored = kind === 'image' ? await uploadRoomImage(f) : await uploadRoomDocument(f);
        uploaded.push({ url: stored.url, name: stored.name || f.name });
      }
      if (kind === 'image') {
        setImages(prev => [...prev, ...uploaded]);
        if (!coverImageUrl && uploaded[0]?.url) setCoverImageUrl(uploaded[0].url);
      } else {
        setDocuments(prev => [...prev, ...uploaded]);
      }
      toast.success('Upload erfolgreich');
    } catch (e: any) {
      toast.error(e?.message || 'Upload fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <WizardShell
        open={open}
        onOpenChange={(v) => {
          if (!v) requestClose();
          else onOpenChange(true);
        }}
        title="Neuen Raum anlegen"
        description={`Schritt ${step} von 7 · Standort: ${standortName}`}
        currentStep={step}
        steps={steps as any}
        onCancel={requestClose}
        onBack={step > 1 ? goBack : undefined}
        canBack={step > 1 && !isLoading}
        onNext={step < 7 ? goNext : undefined}
        canNext={!isLoading && step < 7 ? canNext : false}
        onPrimary={step === 7 ? finalize : undefined}
        primaryLabel={isLoading ? 'Speichere...' : 'Raum erstellen'}
        canPrimary={step === 7 ? canFinish && !isLoading : false}
        isLoading={isLoading}
      >
        <div className="py-4 space-y-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Raumname *</Label>
                <Input
                  placeholder="z. B. Proberaum A, Studio Blau"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <Label>Notiz (intern, optional)</Label>
                <Textarea
                  placeholder='z. B. "Kann für Bandunterricht genutzt werden"'
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="addr-same-standort"
                  checked={addressSameAsStandort}
                  onCheckedChange={(v) => setAddressSameAsStandort(v === true)}
                />
                <Label htmlFor="addr-same-standort">Adresse ist identisch mit dem Standort des Unternehmens</Label>
              </div>

              <div className="relative">
                <Label>Adresse suchen (Vorschläge)</Label>
                <Input
                  placeholder="Adresse eingeben…"
                  value={addressSearch}
                  onChange={(e) => {
                    setAddressSearch(e.target.value);
                    setAddressDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setAddressSearchFocused(true);
                    setAddressDropdownOpen(true);
                  }}
                  onBlur={() => {
                    setAddressSearchFocused(false);
                    window.setTimeout(() => setAddressDropdownOpen(false), 120);
                  }}
                  autoComplete="off"
                  name="room-address-search"
                />
                {!apiKeyPresent && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Hinweis: Kein `VITE_GEOAPIFY_KEY` gesetzt – Adressvorschläge sind deaktiviert.
                  </div>
                )}
                {addressSearchFocused && addressDropdownOpen && addressSuggestions.length > 0 && (
                  <div className="absolute z-20 mt-2 max-h-52 w-full overflow-auto rounded-md border bg-background shadow">
                    {addressSuggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-muted"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setAddressFormatted(s.structured.formatted);
                          setStreet(s.structured.street || '');
                          setHouseNumber(s.structured.houseNumber || '');
                          setPostalCode(s.structured.postalCode || '');
                          setCity(s.structured.city || '');
                          setAddressSearch(s.structured.formatted || s.label || '');
                          setAddressDropdownOpen(false);
                          setAddressSearchFocused(false);
                        }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Straße *</Label>
                  <Input value={street} onChange={(e) => setStreet(e.target.value)} />
                </div>
                <div>
                  <Label>Hausnummer *</Label>
                  <Input value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} />
                </div>
                <div>
                  <Label>PLZ *</Label>
                  <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                </div>
                <div>
                  <Label>Ort *</Label>
                  <Input value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
              </div>

              <div>
                <Label>Wegbeschreibung (optional)</Label>
                <Textarea
                  placeholder="z. B. Hinterhof, zweite Etage, Klingel links, separater Eingang"
                  value={directions}
                  onChange={(e) => setDirections(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="not-public"
                  checked={notPublicAccessible}
                  onCheckedChange={(v) => setNotPublicAccessible(v === true)}
                />
                <Label htmlFor="not-public">Raum ist nicht öffentlich zugänglich (optional)</Label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label>Maximale Personenanzahl *</Label>
                <Input
                  type="number"
                  min={1}
                  value={capacity}
                  onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value) || 1))}
                />
                <div className="mt-2 text-xs text-muted-foreground">
                  Diese Angabe begrenzt die Nutzung und verhindert Überbelegung.
                </div>
              </div>
              <div>
                <Label>Größe (optional, m²)</Label>
                <Input
                  type="number"
                  min={0}
                  value={sizeM2}
                  onChange={(e) => setSizeM2(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm text-muted-foreground">
                Raumkosten dienen ausschließlich internen Auswertungen und haben keine Auswirkungen auf Kunden, Preise oder Abrechnungen.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Interne Raumkosten (optional)</Label>
                  <Input placeholder="z. B. 25,00" value={costEuros} onChange={(e) => setCostEuros(e.target.value)} />
                </div>
                <div>
                  <Label>Intervall</Label>
                  <Select value={costInterval || ''} onValueChange={(v) => setCostInterval((v as any) || '')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Bitte auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hour">pro Stunde</SelectItem>
                      <SelectItem value="day">pro Tag</SelectItem>
                      <SelectItem value="usage">pro Nutzung</SelectItem>
                      <SelectItem value="month">monatlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Wählen Sie die Ausstattung aus. Deaktivierte Tags bleiben aus historischen Gründen sichtbar.
              </div>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((t) => {
                  const isActive = (t as any).is_active !== 0 && (t as any).is_active !== false;
                  const selected = tagIds.includes(t.id);
                  return (
                    <Button
                      key={t.id}
                      type="button"
                      variant={selected ? 'default' : 'outline'}
                      disabled={!isActive && !selected}
                      onClick={() => toggleTag(t.id)}
                    >
                      {t.name}
                      {!isActive ? <span className="ml-2 text-xs opacity-70">(deaktiviert)</span> : null}
                    </Button>
                  );
                })}
                {availableTags.length === 0 && <div className="text-sm text-muted-foreground">Keine Tags vorhanden.</div>}
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Raumbilder hochladen (optional)</Label>
                <Input type="file" accept="image/*" multiple onChange={(e) => void uploadFiles(e.target.files, 'image')} />
                {images.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Titelbild</div>
                    <Select value={coverImageUrl || ''} onValueChange={(v) => setCoverImageUrl(v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Titelbild wählen (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {images.map((img) => (
                          <SelectItem key={img.url} value={img.url}>
                            {img.name || img.url}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Dokumente hochladen (optional)</Label>
                <Input type="file" multiple onChange={(e) => void uploadFiles(e.target.files, 'document')} />
                {documents.length > 0 && (
                  <ul className="list-disc pl-5 text-sm">
                    {documents.map((d) => (
                      <li key={d.url}>{d.name || d.url}</li>
                    ))}
                  </ul>
                )}
                <div className="text-xs text-muted-foreground">
                  Hinweis: Dokumente sind ausschließlich intern sichtbar (Rechteverwaltung).
                </div>
              </div>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-6">
              <div className="rounded-md border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Grunddaten</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStep(1)}>
                    Bearbeiten
                  </Button>
                </div>
                <div className="text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span> {name || '-'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Notiz:</span> {internalNote || '-'}
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Standort & Erreichbarkeit</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStep(2)}>
                    Bearbeiten
                  </Button>
                </div>
                <div className="text-sm">
                  <div>
                    <span className="text-muted-foreground">Adresse:</span>{' '}
                    {street && postalCode ? `${street} ${houseNumber}, ${postalCode} ${city}` : '-'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Wegbeschreibung:</span> {directions || '-'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Nicht öffentlich:</span> {notPublicAccessible ? 'Ja' : 'Nein'}
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Kapazität & Raumdaten</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStep(3)}>
                    Bearbeiten
                  </Button>
                </div>
                <div className="text-sm">
                  <div>
                    <span className="text-muted-foreground">Kapazität:</span> {capacity} Personen
                  </div>
                  <div>
                    <span className="text-muted-foreground">Größe:</span> {sizeM2 === '' ? '-' : `${sizeM2} m²`}
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Kosten</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStep(4)}>
                    Bearbeiten
                  </Button>
                </div>
                <div className="text-sm">
                  <div>
                    <span className="text-muted-foreground">Kosten:</span>{' '}
                    {costEuros ? `${costEuros} EUR` : '-'} {costInterval ? `(${costInterval})` : ''}
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Ausstattung</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStep(5)}>
                    Bearbeiten
                  </Button>
                </div>
                <div className="text-sm">
                  {tagIds.length === 0 ? (
                    <div className="text-muted-foreground">Keine Ausstattung ausgewählt.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {tagIds.map((id) => (
                        <span key={id} className="rounded-full border px-2 py-1 text-xs">
                          {availableTags.find((t) => t.id === id)?.name || `#${id}`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-md border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Bilder & Dokumente</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStep(6)}>
                    Bearbeiten
                  </Button>
                </div>
                <div className="text-sm">
                  <div>
                    <span className="text-muted-foreground">Bilder:</span> {images.length > 0 ? images.length : '-'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dokumente:</span> {documents.length > 0 ? documents.length : '-'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </WizardShell>

      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wizard abbrechen?</DialogTitle>
            <DialogDescription>
              Möchten Sie den aktuellen Stand als Entwurf speichern, damit Sie später am letzten Schritt fortsetzen können?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setConfirmCloseOpen(false);
              }}
            >
              Weiter bearbeiten
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={isLoading}
                onClick={async () => {
                  const saved = await saveDraft(step);
                  if (!saved) return;
                  setConfirmCloseOpen(false);
                  onOpenChange(false);
                }}
              >
                Als Entwurf speichern
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  setConfirmCloseOpen(false);
                  onOpenChange(false);
                }}
              >
                Verwerfen
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RoomCreateWizardModal;


